<script setup lang="ts">
/**
 * A month as a raster of days with their entries as pills — the month view of
 * a calendar, a deadline board. The raster is `NxCalendar`'s (`buildMonthCells`,
 * ISO week numbers, the same key map); what this adds is content per day.
 *
 * Every day is a grid cell on one roving tab stop: arrows walk days and weeks,
 * Home/End the week, PageUp/PageDown the month — each step is emitted as
 * `update:selectedDate`, so the consumer follows into the next month. A click
 * selects, a double-click asks to `create` an entry on that day. Days of the
 * adjacent months are dimmed and only selectable.
 *
 * A busy day scrolls inside its cell; with `maxEvents` it shows that many
 * pills and a „+N weitere“ button instead (`more`). `rangeSelect` lets the
 * pointer sweep several days and reports them as one `rangeSelect`.
 *
 * `layout="lanes"` draws a week row the way a desktop calendar does: all-day
 * and day-long entries are bars across their days (one lane per bar, flat
 * where they go on into the next or previous week), timed entries are lines
 * with a dot under them, and a day with more than fits ends in „+N weitere“.
 * The lanes that fit are measured from the row height (or `maxEvents`), and
 * the entries take `NxEventChip`'s desktop look.
 * `rowHeight` gives every week a fixed height for a host that scrolls the
 * view; the weekday head then sticks. `dimPast` fades entries that are over.
 */
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { buildMonthCells, DAYS_PER_WEEK, isoWeekNumber, parseIso } from '../Calendar/calendarGrid';
import { dayKeyAction } from '../Calendar/calendarKeys';
import { dateTimeFormat } from '../Format/intlCache';
import { i18nInjectionKey } from '../internal';
import NxEventChip, { type NxEventChipStatusLabels } from './NxEventChip.vue';
import { addLocalDays, groupEventsByDay, fromIsoDate, isEventPast, toIsoDate } from './eventDays';
import { layoutMonthWeek, type NxMonthWeekLayout } from './monthLayout';
import type {
  NxCalendarDrop,
  NxCalendarEvent,
  NxCalendarEventKey,
  NxCalendarSlot,
  NxEventDragMode,
  NxEventDragStart,
} from './types';
import { useNow } from './useNow';

import type { NxCalendarEventAppearance } from './types';

const props = withDefaults(
  defineProps<{
    /** ISO `YYYY-MM-DD` of any day in the month to show. */
    month: string;
    /** Optional consecutive-week window instead of a calendar month. */
    startDate?: string;
    weekCount?: number;
    events: NxCalendarEvent[];
    eventAppearance?: NxCalendarEventAppearance;
    /** The selected day; also the grid's tab stop. `v-model:selected-date`. */
    selectedDate?: string | null;
    firstDayOfWeek?: 1 | 7;
    showWeekNumbers?: boolean;
    /**
     * Pills per day before „+N weitere“. Unset: all of them, the cell scrolls
     * (`stack`) or as many lanes as the row height has room for (`lanes`).
     */
    maxEvents?: number;
    /** `stack`: pills per day. `lanes`: bars across the days, timed entries as dotted lines. */
    layout?: 'stack' | 'lanes';
    /** A fixed height per week (any CSS length) for a host that scrolls the view. */
    rowHeight?: string;
    /** Fade entries whose end has passed (or that say `past`). */
    dimPast?: boolean;
    /** Sweeping the pointer over several days reports them as `rangeSelect`. */
    rangeSelect?: boolean;
    draggableEvents?: boolean;
    resizableEvents?: boolean;
    /** A drag that began outside this view is in progress: accept its drop. */
    dropActive?: boolean;
    selectedEventId?: NxCalendarEventKey | null;
    locale?: string;
    /** Accessible name of the grid; defaults to month and year. */
    ariaLabel?: string;
    /** `{count}` is replaced by the number of hidden entries. */
    moreLabel?: string;
    weekPrefix?: string;
    eventCountLabel?: (count: number) => string;
    untitledLabel?: string;
    allDayLabel?: string;
    resizeLabel?: string;
    statusLabels?: Partial<NxEventChipStatusLabels>;
  }>(),
  {
    selectedDate: null,
    firstDayOfWeek: 1,
    showWeekNumbers: false,
    maxEvents: undefined,
    layout: 'stack',
    rowHeight: undefined,
    dimPast: false,
    rangeSelect: false,
    draggableEvents: false,
    resizableEvents: false,
    dropActive: false,
    selectedEventId: null,
    locale: undefined,
    ariaLabel: undefined,
    moreLabel: '+{count} weitere',
    weekPrefix: 'KW',
    eventCountLabel: (count: number) => (count === 1 ? '1 Termin' : `${count} Termine`),
    untitledLabel: '(Ohne Titel)',
    allDayLabel: 'Ganztägig',
    resizeLabel: 'Dauer ändern',
    statusLabels: () => ({}),
  }
);

const emit = defineEmits<{
  'update:selectedDate': [date: string];
  dayClick: [date: string, event: MouseEvent | KeyboardEvent];
  /** Double-click on a day of this month: the consumer creates an entry. */
  create: [slot: NxCalendarSlot];
  slotContextmenu: [slot: NxCalendarSlot, event: MouseEvent];
  more: [date: string];
  rangeSelect: [start: string, end: string];
  eventClick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDblclick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventContextmenu: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDragStart: [payload: NxEventDragStart];
  eventDragEnd: [];
  drop: [payload: NxCalendarDrop];
}>();

defineSlots<{
  /** Replaces the default `NxEventChip` of an entry. */
  event?: (props: { event: NxCalendarEvent; date: string }) => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);
const resolvedLocale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');
const now = useNow();
const todayIso = computed(() => toIsoDate(now.value));

const focusMonth = computed(() => {
  const { year, month } = parseIso(props.month);
  const valid = Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12;
  return valid
    ? { year, month }
    : { year: now.value.getFullYear(), month: now.value.getMonth() + 1 };
});

const fullFormat = computed(() =>
  dateTimeFormat(resolvedLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
);

const gridLabel = computed(
  () =>
    props.ariaLabel ??
    dateTimeFormat(resolvedLocale.value, { month: 'long', year: 'numeric' }).format(
      new Date(focusMonth.value.year, focusMonth.value.month - 1, 1)
    )
);

interface MonthViewCell {
  iso: string;
  day: Date;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
  events: NxCalendarEvent[];
  shown: NxCalendarEvent[];
  hidden: number;
  label: string;
}

const cells = computed<MonthViewCell[]>(() => {
  const { year, month } = focusMonth.value;
  const start = props.startDate ? fromIsoDate(props.startDate) : null;
  const grid = start
    ? Array.from({ length: Math.min(12, Math.max(1, props.weekCount ?? 6)) * 7 }, (_, n) => {
        const day = addLocalDays(start, n);
        return {
          year: day.getFullYear(),
          month: day.getMonth() + 1,
          day: day.getDate(),
          iso: toIsoDate(day),
          inMonth: true,
        };
      })
    : buildMonthCells(year, month, props.firstDayOfWeek);
  const first = grid[0];
  const last = grid[grid.length - 1];
  const byDay = new Map(
    groupEventsByDay(props.events, {
      start: new Date(first.year, first.month - 1, first.day),
      end: new Date(last.year, last.month - 1, last.day + 1),
    }).map((entry) => [entry.date, entry.events])
  );
  return grid.map((cell) => {
    const day = new Date(cell.year, cell.month - 1, cell.day);
    const events = byDay.get(cell.iso) ?? [];
    const limit = props.maxEvents;
    const capped = limit !== undefined && limit >= 0 && events.length > limit;
    // A „+1 weitere“ button costs the same line as the pill it hides.
    const shown = capped ? events.slice(0, Math.max(0, limit - 1)) : events;
    return {
      iso: cell.iso,
      day,
      dayNumber: cell.day,
      inMonth: cell.inMonth,
      isToday: cell.iso === todayIso.value,
      events,
      shown,
      hidden: events.length - shown.length,
      label: `${fullFormat.value.format(day)}, ${props.eventCountLabel(events.length)}`,
    };
  });
});

const lanesLayout = computed(() => props.layout === 'lanes');

/** Lanes a cell has room for, measured from the rendered row (see `measureLanes`). */
const measuredLanes = ref<number | null>(null);
const DEFAULT_LANES = 4;
/** Title lines a chip may show (1–4), as the chip itself clamps them. */
const titleLines = computed(() =>
  Math.min(4, Math.max(1, Math.round(props.eventAppearance?.titleLines ?? 1)))
);

const laneCount = computed(() =>
  Math.max(1, Math.floor(props.maxEvents ?? measuredLanes.value ?? DEFAULT_LANES))
);

const weeks = computed(() => {
  const monday = props.firstDayOfWeek === 1 ? 0 : 1;
  const rows: Array<{
    number: number;
    cells: MonthViewCell[];
    lanes: NxMonthWeekLayout | null;
  }> = [];
  for (let start = 0; start < cells.value.length; start += DAYS_PER_WEEK) {
    const rowCells = cells.value.slice(start, start + DAYS_PER_WEEK);
    const anchor = rowCells[monday].day;
    rows.push({
      number: isoWeekNumber(anchor.getFullYear(), anchor.getMonth() + 1, anchor.getDate()),
      cells: rowCells,
      lanes: lanesLayout.value
        ? layoutMonthWeek(
            props.events,
            rowCells.map((cell) => cell.iso),
            laneCount.value
          )
        : null,
    });
  }
  return rows;
});

function isPast(event: NxCalendarEvent): boolean {
  return props.dimPast && isEventPast(event, now.value);
}

const weekdayLabels = computed(() => {
  const format = dateTimeFormat(resolvedLocale.value, { weekday: 'short' });
  // 5 January 2026 is a Monday; offset by the configured week start.
  return Array.from({ length: DAYS_PER_WEEK }, (_, index) => {
    const label = format.format(
      new Date(2026, 0, 5 + (props.firstDayOfWeek === 1 ? index : index - 1))
    );
    // The lanes' head is a row of labels, not abbreviations in a sentence: „Mo“, not „Mo.“.
    return lanesLayout.value ? label.replace(/\.$/, '') : label;
  });
});

/** The tab stop: the selected day when it is on screen, else today, else the 1st. */
const tabStop = computed(() => {
  const inMonth = cells.value.filter((cell) => cell.inMonth);
  const onScreen = (iso: string | null) =>
    iso && cells.value.some((cell) => cell.iso === iso) ? iso : null;
  return (
    onScreen(props.selectedDate) ??
    inMonth.find((cell) => cell.isToday)?.iso ??
    inMonth[0]?.iso ??
    ''
  );
});

function daySlot(cell: MonthViewCell): NxCalendarSlot {
  return { date: cell.iso, start: cell.day, end: addLocalDays(cell.day, 1), allDay: true };
}

// --- selection and keyboard ------------------------------------------------

const root = ref<HTMLElement | null>(null);
const focusPending = ref<string | null>(null);

// --- lanes: how many lines a cell has room for -------------------------------

function measureLanes(): void {
  if (!lanesLayout.value || props.maxEvents !== undefined) return;
  const list = root.value?.querySelector<HTMLElement>('.nx-month-view__lanes');
  const line = list?.querySelector<HTMLElement>('.nx-month-view__lane');
  const room = list?.clientHeight ?? 0;
  const height = line?.offsetHeight ?? 0;
  if (room <= 0 || height <= 0) return;
  const lanes = Math.max(1, Math.floor((room + 0.5) / height));
  if (lanes !== measuredLanes.value) measuredLanes.value = lanes;
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  void nextTick(measureLanes);
  if (typeof ResizeObserver === 'undefined' || !root.value) return;
  resizeObserver = new ResizeObserver(() => measureLanes());
  resizeObserver.observe(root.value);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

watch(
  () => [props.layout, props.rowHeight, props.maxEvents, cells.value.length, titleLines.value],
  () => void nextTick(measureLanes)
);

async function focusDay(iso: string): Promise<void> {
  await nextTick();
  const cell = root.value?.querySelector<HTMLElement>(`[data-date="${iso}"]`);
  if (cell) {
    cell.focus();
    focusPending.value = null;
  }
}

// The consumer may answer a step with a new `month`; the cell exists only then.
watch(
  () => [props.month, props.startDate, props.selectedDate],
  () => {
    if (focusPending.value) void focusDay(focusPending.value);
  }
);

function onCellClick(event: MouseEvent, cell: MonthViewCell): void {
  emit('update:selectedDate', cell.iso);
  emit('dayClick', cell.iso, event);
}

function onCellDblclick(cell: MonthViewCell): void {
  if (!cell.inMonth) return;
  emit('create', daySlot(cell));
}

function onCellContextmenu(event: MouseEvent, cell: MonthViewCell): void {
  if (!cell.inMonth) return;
  emit('slotContextmenu', daySlot(cell), event);
}

function onCellKeydown(event: KeyboardEvent, cell: MonthViewCell): void {
  if (event.target !== event.currentTarget) return;
  if (event.ctrlKey || event.altKey || event.metaKey) return;
  const action = dayKeyAction(event.key, cell.iso, props.firstDayOfWeek);
  if (!action) return;
  event.preventDefault();
  if (action.kind === 'select') {
    emit('update:selectedDate', cell.iso);
    emit('dayClick', cell.iso, event);
    return;
  }
  focusPending.value = action.date;
  emit('update:selectedDate', action.date);
  void focusDay(action.date);
}

// --- range sweep -------------------------------------------------------------

const rangeAnchor = ref<string | null>(null);
const rangeFocus = ref<string | null>(null);

function inRange(iso: string): boolean {
  if (!rangeAnchor.value || !rangeFocus.value) return false;
  const [from, to] = [rangeAnchor.value, rangeFocus.value].sort();
  return iso >= from && iso <= to;
}

function stopRange(): void {
  rangeAnchor.value = null;
  rangeFocus.value = null;
  window.removeEventListener('pointerup', finishRange);
}

function finishRange(): void {
  if (!rangeAnchor.value || !rangeFocus.value) return stopRange();
  const [from, to] = [rangeAnchor.value, rangeFocus.value].sort();
  stopRange();
  if (from !== to) emit('rangeSelect', from, to);
}

function onCellPointerDown(event: PointerEvent, cell: MonthViewCell): void {
  if (!props.rangeSelect || !cell.inMonth || event.button !== 0) return;
  if ((event.target as HTMLElement | null)?.closest?.('button, a, input, select, textarea')) return;
  rangeAnchor.value = cell.iso;
  rangeFocus.value = cell.iso;
  window.addEventListener('pointerup', finishRange);
}

function onCellPointerEnter(cell: MonthViewCell): void {
  if (rangeAnchor.value && cell.inMonth) rangeFocus.value = cell.iso;
}

onBeforeUnmount(stopRange);

// --- drag and drop ---------------------------------------------------------

const dragOperation = ref<NxEventDragStart | null>(null);
/** Set a tick after dragstart: changing the DOM inside dragstart cancels the drag. */
const dragging = ref(false);
const dropDate = ref<string | null>(null);
const acceptsDrop = computed(() => dragOperation.value !== null || props.dropActive);

function onEventDragStart(nativeEvent: DragEvent, event: NxCalendarEvent): void {
  const fromHandle = Boolean(
    (nativeEvent.target as HTMLElement | null)?.closest?.('[data-resize-handle]')
  );
  const mode: NxEventDragMode = fromHandle ? 'resize' : 'move';
  const allowed =
    !event.readonly && (mode === 'move' ? props.draggableEvents : props.resizableEvents);
  if (!allowed) return;
  if (nativeEvent.dataTransfer) {
    nativeEvent.dataTransfer.effectAllowed = 'move';
    if (!nativeEvent.dataTransfer.types?.length) {
      nativeEvent.dataTransfer.setData('text/plain', String(event.id));
    }
  }
  dragOperation.value = { event, mode };
  emit('eventDragStart', { event, mode });
  setTimeout(() => {
    dragging.value = dragOperation.value !== null;
  }, 0);
}

function onEventDragEnd(): void {
  dragOperation.value = null;
  dragging.value = false;
  dropDate.value = null;
  emit('eventDragEnd');
}

function onCellDragOver(event: DragEvent, cell: MonthViewCell): void {
  if (!acceptsDrop.value || !cell.inMonth) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  dropDate.value = cell.iso;
}

/** Time of day of `source`, moved onto `day` — a move keeps the clock time. */
function atTimeOf(day: Date, source: Date): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    source.getHours(),
    source.getMinutes(),
    source.getSeconds()
  );
}

function onCellDrop(event: DragEvent, cell: MonthViewCell): void {
  if (!acceptsDrop.value || !cell.inMonth) return;
  event.preventDefault();
  const operation = dragOperation.value;
  let start: Date | null = null;
  let end: Date | null = null;
  if (operation) {
    const source = operation.event;
    const hasEnd = source.end instanceof Date && source.end.getTime() > source.start.getTime();
    if (operation.mode === 'resize') {
      start = source.start;
      end = source.allDay
        ? addLocalDays(cell.day, 1)
        : atTimeOf(cell.day, hasEnd ? source.end! : source.start);
      if (end.getTime() <= start.getTime()) end = hasEnd ? source.end! : source.start;
    } else if (source.allDay) {
      const days = hasEnd
        ? Math.max(1, Math.round((source.end!.getTime() - source.start.getTime()) / 86_400_000))
        : 1;
      start = cell.day;
      end = addLocalDays(cell.day, days);
    } else {
      start = atTimeOf(cell.day, source.start);
      end = new Date(
        start.getTime() + (hasEnd ? source.end!.getTime() - source.start.getTime() : 0)
      );
    }
  }
  emit('drop', {
    slot: daySlot(cell),
    event: operation?.event ?? null,
    mode: operation?.mode ?? null,
    start,
    end,
  });
  dragOperation.value = null;
  dragging.value = false;
  dropDate.value = null;
}

function cellClasses(cell: MonthViewCell): Record<string, boolean> {
  return {
    'is-outside': !cell.inMonth,
    'is-today': cell.isToday,
    'is-selected': cell.iso === props.selectedDate,
    'is-in-range': cell.inMonth && inRange(cell.iso),
    'is-drop-target': dropDate.value === cell.iso,
    'is-capped': props.maxEvents !== undefined,
  };
}

function moreText(count: number): string {
  return props.moreLabel.replace('{count}', String(count));
}

const rootStyle = computed(() => ({
  ...(props.rowHeight ? { '--month-row-height': props.rowHeight } : {}),
  '--month-title-lines': String(titleLines.value),
}));

function monthLabel(cell: MonthViewCell): string {
  return dateTimeFormat(resolvedLocale.value, { day: 'numeric', month: 'short' }).format(cell.day);
}

/** The first of a month gets its month name: always while rolling, outside the shown month too. */
function showsMonthLabel(cell: MonthViewCell): boolean {
  return cell.dayNumber === 1 && Boolean(props.startDate || lanesLayout.value);
}

const chipProps = computed(() => ({
  appearance: props.eventAppearance,
  look: lanesLayout.value ? ('desktop' as const) : ('classic' as const),
  draggable: props.draggableEvents,
  resizable: props.resizableEvents,
  locale: resolvedLocale.value,
  untitledLabel: props.untitledLabel,
  allDayLabel: props.allDayLabel,
  resizeLabel: props.resizeLabel,
  statusLabels: props.statusLabels,
}));
</script>

<template>
  <div
    ref="root"
    :aria-label="gridLabel"
    :class="[
      'nx-month-view',
      {
        'has-weeks': showWeekNumbers,
        'is-lanes': lanesLayout,
        'has-row-height': Boolean(rowHeight),
        'is-dragging': dragging || dropActive,
        'is-sweeping': rangeAnchor !== null,
      },
    ]"
    role="grid"
    :style="rootStyle"
  >
    <div class="nx-month-view__row nx-month-view__head" role="row">
      <div v-if="showWeekNumbers" class="nx-month-view__week-head" role="columnheader">
        {{ weekPrefix }}
      </div>
      <div
        v-for="(weekday, index) in weekdayLabels"
        :key="`wd-${index}`"
        class="nx-month-view__weekday"
        role="columnheader"
      >
        {{ weekday }}
      </div>
    </div>

    <div v-for="week in weeks" :key="week.cells[0].iso" class="nx-month-view__row" role="row">
      <div v-if="showWeekNumbers" class="nx-month-view__week" role="rowheader">
        {{ week.number }}
      </div>
      <div
        v-for="(cell, column) in week.cells"
        :key="cell.iso"
        :aria-label="cell.label"
        :aria-selected="cell.iso === selectedDate"
        :class="['nx-month-view__cell', cellClasses(cell)]"
        :data-date="cell.iso"
        role="gridcell"
        :tabindex="cell.iso === tabStop ? 0 : -1"
        @click="onCellClick($event, cell)"
        @contextmenu="onCellContextmenu($event, cell)"
        @dblclick="onCellDblclick(cell)"
        @dragleave="dropDate = null"
        @dragover="onCellDragOver($event, cell)"
        @drop="onCellDrop($event, cell)"
        @keydown="onCellKeydown($event, cell)"
        @pointerdown="onCellPointerDown($event, cell)"
        @pointerenter="onCellPointerEnter(cell)"
      >
        <span
          class="nx-month-view__daynum"
          :class="{ 'has-month-label': showsMonthLabel(cell) }"
          :aria-current="cell.isToday ? 'date' : undefined"
        >
          {{ showsMonthLabel(cell) ? monthLabel(cell) : cell.dayNumber }}
        </span>
        <ul v-if="week.lanes" class="nx-month-view__lanes">
          <li
            v-for="(slot, lane) in week.lanes.days[column].slots"
            :key="`${cell.iso}-lane-${lane}`"
            class="nx-month-view__lane"
          >
            <div
              v-if="slot?.kind === 'bar' && week.lanes.bars[slot.bar].column === column"
              :class="[
                'nx-month-view__bar',
                {
                  'continues-before': week.lanes.bars[slot.bar].continuesBefore,
                  'continues-after': week.lanes.bars[slot.bar].continuesAfter,
                },
              ]"
              :style="{ '--bar-span': String(week.lanes.bars[slot.bar].span) }"
              @click.stop="emit('eventClick', week.lanes.bars[slot.bar].event, $event)"
              @contextmenu.stop="emit('eventContextmenu', week.lanes.bars[slot.bar].event, $event)"
              @dblclick.stop="emit('eventDblclick', week.lanes.bars[slot.bar].event, $event)"
              @dragend="onEventDragEnd"
              @dragstart="onEventDragStart($event, week.lanes.bars[slot.bar].event)"
              @pointerdown.stop
            >
              <slot :date="cell.iso" :event="week.lanes.bars[slot.bar].event" name="event">
                <NxEventChip
                  v-bind="chipProps"
                  :event="week.lanes.bars[slot.bar].event"
                  :past="isPast(week.lanes.bars[slot.bar].event)"
                  :selected="
                    selectedEventId !== null &&
                    selectedEventId === week.lanes.bars[slot.bar].event.id
                  "
                  :show-time="false"
                  size="compact"
                />
              </slot>
            </div>
            <div
              v-else-if="slot?.kind === 'line'"
              class="nx-month-view__line"
              @click.stop="emit('eventClick', slot.event, $event)"
              @contextmenu.stop="emit('eventContextmenu', slot.event, $event)"
              @dblclick.stop="emit('eventDblclick', slot.event, $event)"
              @dragend="onEventDragEnd"
              @dragstart="onEventDragStart($event, slot.event)"
              @pointerdown.stop
            >
              <slot :date="cell.iso" :event="slot.event" name="event">
                <NxEventChip
                  v-bind="chipProps"
                  :event="slot.event"
                  :past="isPast(slot.event)"
                  :selected="selectedEventId !== null && selectedEventId === slot.event.id"
                  size="compact"
                  variant="dot"
                />
              </slot>
            </div>
            <button
              v-else-if="!slot && lane === laneCount - 1 && week.lanes.days[column].hidden > 0"
              class="nx-month-view__more"
              type="button"
              @click.stop="emit('more', cell.iso)"
              @dblclick.stop
              @pointerdown.stop
            >
              {{ moreText(week.lanes.days[column].hidden) }}
            </button>
          </li>
        </ul>
        <template v-else>
          <ul class="nx-month-view__events">
            <li
              v-for="event in cell.shown"
              :key="`${cell.iso}-${event.id}`"
              class="nx-month-view__event"
              @click.stop="emit('eventClick', event, $event)"
              @contextmenu.stop="emit('eventContextmenu', event, $event)"
              @dblclick.stop="emit('eventDblclick', event, $event)"
              @dragend="onEventDragEnd"
              @dragstart="onEventDragStart($event, event)"
              @pointerdown.stop
            >
              <slot :date="cell.iso" :event="event" name="event">
                <NxEventChip
                  v-bind="chipProps"
                  :event="event"
                  :past="isPast(event)"
                  :selected="selectedEventId !== null && selectedEventId === event.id"
                  size="compact"
                />
              </slot>
            </li>
          </ul>
          <button
            v-if="cell.hidden > 0"
            class="nx-month-view__more"
            type="button"
            @click.stop="emit('more', cell.iso)"
            @dblclick.stop
            @pointerdown.stop
          >
            {{ moreText(cell.hidden) }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A framed table like NxCalendar's month: the rules are the 1px gaps letting
   the frame colour through, so every cell carries an opaque background. */
.nx-month-view {
  --month-columns: repeat(7, minmax(0, 1fr));
  /* One line of the lanes layout: a bar or a timed entry. */
  --month-title-lines: 1;
  /* …one title line tall, plus a chip line (0.8125 units × tight line height)
     for each further title line the appearance asks for. */
  --month-line-height: calc(
    var(--nxt-size-unit) * 1.375 + (var(--month-title-lines) - 1) * var(--nxt-size-unit) * 0.8125 *
      var(--nxt-line-height-tight)
  );
  display: grid;
  flex: 1;
  grid-template-rows: auto;
  grid-auto-rows: minmax(0, 1fr);
  gap: var(--nxt-hairline);
  box-sizing: border-box;
  min-height: calc(var(--nxt-size-unit) * 30);
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-border);
  color: var(--nxt-fg);
}

.nx-month-view.has-weeks {
  --month-columns: calc(var(--nxt-size-unit) * 2.5) repeat(7, minmax(0, 1fr));
}

.nx-month-view.is-lanes.has-weeks {
  --month-columns: calc(var(--nxt-size-unit) * 2) repeat(7, minmax(0, 1fr));
}

/* Fixed weeks for a scrolling host: the view keeps its height, the head sticks. */
.nx-month-view.has-row-height {
  flex: none;
  grid-auto-rows: var(--month-row-height);
  overflow: visible;
}

.nx-month-view.has-row-height .nx-month-view__head {
  position: sticky;
  top: 0;
  z-index: 2;
}

.nx-month-view__row {
  display: grid;
  grid-template-columns: var(--month-columns);
  gap: var(--nxt-hairline);
  min-height: 0;
}

.nx-month-view__weekday,
.nx-month-view__week-head,
.nx-month-view__week {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--nxt-space-xs) 0;
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
}

.nx-month-view__week {
  font-weight: var(--nxt-font-weight-regular);
  font-variant-numeric: tabular-nums;
}

/* Lanes: the weekdays as quiet labels over the start of each day, the week
   numbers centred in their column. */
.nx-month-view.is-lanes .nx-month-view__weekday {
  justify-content: flex-start;
  padding-inline: var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  font-weight: var(--nxt-font-weight-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nx-month-view.is-lanes .nx-month-view__week-head,
.nx-month-view.is-lanes .nx-month-view__week {
  font-weight: var(--nxt-font-weight-regular);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* The week number sits on the line of the day numbers. */
.nx-month-view.is-lanes .nx-month-view__week {
  align-items: flex-start;
  padding-block: var(--nxt-space-xs) 0;
}

.nx-month-view__cell {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  min-height: 0;
  padding: var(--nxt-space-2xs);
  overflow: hidden;
  outline: none;
  background: var(--nxt-surface-bg);
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-month-view__cell:hover {
  background: var(--nxt-surface-sunken-bg);
}

.nx-month-view__cell:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}

.nx-month-view__cell.is-outside {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-subtle);
}

.nx-month-view__cell.is-selected,
.nx-month-view__cell.is-in-range,
.nx-month-view__cell.is-drop-target {
  background: color-mix(in srgb, var(--nxt-brand-bg) 8%, var(--nxt-surface-bg));
}

.nx-month-view__cell.is-selected {
  box-shadow: inset 0 0 0 var(--nxt-hairline)
    color-mix(in srgb, var(--nxt-brand-bg) 35%, transparent);
}

.nx-month-view__daynum {
  display: inline-grid;
  place-items: center;
  align-self: flex-end;
  flex: none;
  width: var(--nxt-icon-size-lg);
  height: var(--nxt-icon-size-lg);
  border-radius: var(--nxt-radius-full);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  font-variant-numeric: tabular-nums;
}

.nx-month-view__daynum.has-month-label {
  width: auto;
  padding-inline: var(--nxt-space-xs);
  white-space: nowrap;
}

.nx-month-view__cell.is-today .nx-month-view__daynum {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}

/* Lanes: the day number top left, regular weight, today on the brand disc. */
.nx-month-view.is-lanes .nx-month-view__daynum {
  align-self: flex-start;
  width: auto;
  min-width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  box-sizing: border-box;
  padding-inline: var(--nxt-space-2xs);
  font-weight: var(--nxt-font-weight-regular);
}

.nx-month-view.is-lanes .nx-month-view__daynum.has-month-label {
  padding-inline: var(--nxt-space-xs);
}

.nx-month-view.is-lanes .nx-month-view__cell.is-outside .nx-month-view__daynum {
  color: var(--nxt-fg-subtle);
}

.nx-month-view.is-lanes .nx-month-view__cell.is-today .nx-month-view__daynum {
  color: var(--nxt-brand-fg);
  font-weight: var(--nxt-font-weight-medium);
}

/* The day's pills scroll inside the cell instead of being clipped. */
.nx-month-view__events {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--nxt-hairline);
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  font-size: inherit;
  scrollbar-width: thin;
  scrollbar-color: var(--nxt-border-strong) transparent;
}

.is-capped .nx-month-view__events {
  overflow: hidden;
}

.nx-month-view__event {
  min-width: 0;
}

/* --- lanes: bars across the week, timed entries as lines ---------------- */

/* Bars reach sideways into the next cells; downwards the cell still clips. */
.nx-month-view.is-lanes .nx-month-view__cell {
  gap: 0;
  padding-inline: 0;
  overflow-x: visible;
  overflow-y: clip;
}

.nx-month-view.is-lanes .nx-month-view__daynum {
  margin-inline-start: var(--nxt-space-2xs);
}

.nx-month-view__lanes {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-x: visible;
  overflow-y: clip;
  list-style: none;
  font-size: inherit;
}

.nx-month-view__lane {
  position: relative;
  height: var(--month-line-height);
  min-width: 0;
}

/* A bar starts in its first cell of the week and reaches over the rules into
   the next ones; it stays flat where it goes on beyond the week. */
.nx-month-view__bar {
  position: absolute;
  inset-block: var(--nxt-hairline);
  inset-inline-start: 0;
  z-index: 1;
  box-sizing: border-box;
  width: calc(var(--bar-span) * 100% + (var(--bar-span) - 1) * var(--nxt-hairline));
  padding-inline: var(--nxt-space-2xs);
}

.nx-month-view__bar.continues-before {
  padding-inline-start: 0;
}

.nx-month-view__bar.continues-after {
  padding-inline-end: 0;
}

.nx-month-view__line {
  height: 100%;
  padding-inline: var(--nxt-space-2xs);
}

.nx-month-view__bar :deep(.nx-event-chip),
.nx-month-view__line :deep(.nx-event-chip) {
  height: 100%;
  min-height: 0;
  padding-block: 0;
}

.nx-month-view__bar.continues-before :deep(.nx-event-chip) {
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}

.nx-month-view__bar.continues-after :deep(.nx-event-chip) {
  border-start-end-radius: 0;
  border-end-end-radius: 0;
}

.nx-month-view.is-lanes .nx-month-view__more {
  height: 100%;
  margin-inline-start: var(--nxt-space-2xs);
}

/* While an entry is dragged or days are swept, bars and lines let the pointer
   through to the day under them — a bar's element belongs to its first day. */
.nx-month-view.is-dragging .nx-month-view__bar,
.nx-month-view.is-dragging .nx-month-view__line,
.nx-month-view.is-sweeping .nx-month-view__bar,
.nx-month-view.is-sweeping .nx-month-view__line {
  pointer-events: none;
}

.nx-month-view__more {
  flex: none;
  align-self: flex-start;
  padding: 0 var(--nxt-space-2xs);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  cursor: pointer;
}

.nx-month-view__more:hover {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}

.nx-month-view__more:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
}

@media (forced-colors: active) {
  .nx-month-view__cell {
    outline: var(--nxt-hairline) solid canvastext;
    outline-offset: calc(var(--nxt-hairline) * -1);
  }

  .nx-month-view__cell.is-today .nx-month-view__daynum {
    forced-color-adjust: none;
    background: highlight;
    color: highlighttext;
  }
}
</style>
